import { useEffect, useRef } from "react";
import * as E from "@numi/engine";
import { S, useApp, go, set, stickersOwned } from "../store.ts";
import { Card, Icon, Numi, Star, burstAt } from "../ui.tsx";
import { SFX } from "../sound.ts";
import { startRound, startLearn, sayAloud } from "../question.ts";
import { startBubbles } from "./Bubbles.tsx";
import { runStep, nextStepIndex, nextVedic, abacusNextAction } from "../plan.ts";

export function Done() {
  const s = useApp(); const r = s.result!; const owned = stickersOwned(); const toNext = owned >= E.STICKERS.length ? 0 : 5 - (s.totalStars % 5);
  const brk = useRef(E.pick(E.BREAKS)); const ref = useRef<HTMLDivElement>(null);
  // Continuous-flow helpers (evaluated once at render time)
  const isAbacus = s.topic === "abacus";
  const abacusNext = isAbacus ? abacusNextAction() : null;
  const topicMod = s.topic ? E.topic(s.topic)?.mod : null;
  const isVedic = topicMod === "vedic";
  const vedicNext = isVedic ? nextVedic() : null;
  let head: string, sub: string;
  if (r.kind === "round") {
    head = r.right === r.of ? "All right first time." : r.right! >= Math.ceil(r.of! * 0.6) ? "Good work." : "Round finished.";
    sub = r.right === 0 ? `You worked through all ${r.of}. Every one you checked teaches you something.` : `You got ${r.right} of ${r.of} right on the first try.`;
    if (r.prev != null && r.right! > r.prev) sub += ` That's ${r.right! - r.prev} more than last time.`;
    else if (r.prev != null && r.right === r.prev && r.right! < r.of!) sub += " Same as last time. A bit more practice and it will click.";
  } else { head = r.score! >= 10 ? "Brilliant popping." : "Nice popping."; sub = `You popped ${r.score} right answer${r.score === 1 ? "" : "s"} and won ${r.stars} star${r.stars === 1 ? "" : "s"}.`; }
  const plan = s.planJustDone != null && s.plan ? s.plan : null; const ni = plan ? nextStepIndex(plan) : -1; const nextStep = plan && ni >= 0 ? plan.steps[ni] : null;
  // Mental math tip: the strategy used for this topic (not shown for mixed/bubble rounds)
  const tip = r.kind === "round" && !r.mixed && s.topic ? E.STRAT[s.topic] : null;
  useEffect(() => { if (r.sticker != null) setTimeout(() => SFX.sticker(), 250); setTimeout(() => burstAt(ref.current, 22), 150); sayAloud(head + " " + sub); }, []);
  return <main><Card className="done">
    <div ref={ref}><Numi size={120} mood="fly cheer" /></div><h2>{head}</h2><p style={{ fontSize: "1.25rem", margin: 0, maxWidth: "34em" }}>{sub}</p>
    {r.kind === "round" && <div className="bigstars">{Array.from({ length: r.of! }, (_, i) => <Star key={i} on={i < r.right!} />)}</div>}
    {r.sticker != null ? <><div className="newsticker" aria-hidden="true">{E.STICKERS[r.sticker][0]}</div><p style={{ margin: 0, fontWeight: 700, fontSize: "1.2rem" }}>New sticker: {E.STICKERS[r.sticker][1]}!</p></>
      : owned < E.STICKERS.length && <><div className="sbar" aria-label="Sticker progress"><i style={{ width: `${((5 - toNext) / 5) * 100}%` }} /></div><p className="note">{toNext} more star{toNext === 1 ? "" : "s"} to your next sticker</p></>}
    {tip && <div className="strat"><span className="strat-ic" aria-hidden="true">💡</span><span><b>The trick you used:</b> {tip}</span></div>}
    {r.brk && <div className="brk"><b>Movement break:</b> {brk.current}</div>}
    {nextStep
      /* ── Inside today's plan: advance to the next plan step ── */
      ? <div className="row center">
          <button className="btn yellow big" onClick={() => runStep(ni)}>Next: {nextStep.title} <Icon name="next" /></button>
          <button className="btn big" onClick={() => go("home")}><Icon name="home" /> Home</button>
        </div>
      /* ── Abacus: progress through levels then Bubble Pop ── */
      : isAbacus
        ? <div className="row center">
            {abacusNext === "level"
              ? <button className="btn yellow big" onClick={() => { set({ topic: "abacus" }); startRound(); }}>Next level <Icon name="next" /></button>
              : abacusNext === "bubbles"
                ? <button className="btn yellow big" onClick={() => startBubbles(false)}>Bubble Pop on Abacus <Icon name="bubbles" /></button>
                : <button className="btn yellow big" onClick={() => startRound()}><Icon name="again" /> Play again</button>}
            <button className="btn big" onClick={() => go("home")}><Icon name="home" /> Home</button>
          </div>
      /* ── Vedic: progress to the next trick ── */
      : isVedic
        ? <div className="row center">
            {vedicNext
              ? <button className="btn yellow big" onClick={() => { set({ topic: vedicNext.id }); go("topic"); }}>Next: {E.topicTitle(vedicNext)} <Icon name="next" /></button>
              : <button className="btn yellow big" onClick={() => go("vedic")}><Icon name="flag" /> All tricks done!</button>}
            <button className="btn big" onClick={() => go("home")}><Icon name="home" /> Home</button>
          </div>
      /* ── Normal topic ── */
      : <div className="row center">
          {!r.mixed || r.kind !== "round" ? <button className="btn yellow big" onClick={() => r.kind === "round" ? startRound() : startBubbles(r.mixed)}><Icon name="again" /> Play again</button> : null}
          {r.kind === "round" && !r.mixed && <button className="btn big" onClick={() => startLearn()}><Icon name="book" /> Learn again</button>}
          <button className="btn big" onClick={() => go("home")}><Icon name="home" /> Home</button>
        </div>}
  </Card></main>;
}

export function Stickers() {
  const s = useApp(); const owned = stickersOwned();
  return <main>
    <Card><h2 style={{ fontSize: "2rem" }}>My sticker book</h2><p className="note" style={{ fontSize: "1rem" }}>Every 5 stars wins a new sticker. You have {s.totalStars} star{s.totalStars === 1 ? "" : "s"}.</p></Card>
    <div className="book">{E.STICKERS.map((st, i) => i < owned ? <div key={i} className="stk"><span aria-hidden="true">{st[0]}</span><small>{st[1]}</small></div> : <div key={i} className="stk lock" aria-label="Locked sticker">?<small>{(i + 1) * 5} stars</small></div>)}
      {s.child?.settings.friendBadge && <div className="stk friend"><span aria-hidden="true">🤝</span><small>Friend of Numi</small></div>}</div>
  </main>;
}
